import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Paper,
  Divider,
  Card,
  CardContent,
  Snackbar,
  LinearProgress,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Listlet from "../../../components/Listlet/Listlet";
import endpoints from "../../../environment/endpoints";
import FetchService from "../../../services/FetchService";
import { svr_MashList } from "../../../types";
import styles from "./mashlist-manage.module.css";

export interface Props {
  id: string;
}

const fetchService = new FetchService();

export default function MashListManage(props: Props) {
  const [mashList, setMashList] = useState<svr_MashList>({
    id: "-",
    name: "",
    contenders: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Dialog States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRenameListModal, setShowRenameListModal] = useState(false);
  const [showDeleteListModal, setShowDeleteListModal] = useState(false);
  const [renamingContender, setRenamingContender] = useState<{ id: string, title: string } | null>(null);

  const [tempName, setTempName] = useState("");
  const [showCopySnackbar, setShowCopySnackbar] = useState(false);

  // Image upload state
  const [uploadingImageFor, setUploadingImageFor] = useState<string | null>(null);
  const [addContenderImage, setAddContenderImage] = useState<File | null>(null);
  const [addContenderImagePreview, setAddContenderImagePreview] = useState<string | null>(null);


  const fetchList = useCallback(() => {
    setIsLoading(true);
    fetchService
      .get(endpoints.getMashList, props.id)
      .then((res) => {
        if (res.success) {
          setMashList(res.data);
        } else {
          setError(res.errors || "Failed to load mash list.");
        }
      })
      .finally(() => setIsLoading(false));
  }, [props.id]);

  useEffect(() => {
    props.id && fetchList();
  }, [props.id, fetchList]);

  const handleRenameList = async () => {
    if (!tempName.trim()) return;
    const res = await fetchService.put(endpoints.renameMashList, { title: tempName }, props.id);
    if (res.success) {
      setShowRenameListModal(false);
      fetchList();
    } else {
      alert(res.errors);
    }
  };

  const handleDeleteList = () => {
    fetchService
      .delete(endpoints.deleteMashList, props.id)
      .then((res) => {
        if (res.success) {
          navigate("/mashlist/list");
        } else {
          alert(res.errors);
        }
      });
  };

  const handleAddContender = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = formData.get("name") as string;
    const description = formData.get("description") as string;
    if (!name) return;

    const res = await fetchService.post(endpoints.addContender, { title: name, description }, props.id);
    if (res.success) {
      // If an image was selected, upload it for the new contender
      if (addContenderImage && res.data?.id) {
        await fetchService.uploadFile(endpoints.uploadContenderImage, addContenderImage, res.data.id);
      }
      setShowAddModal(false);
      setAddContenderImage(null);
      setAddContenderImagePreview(null);
      fetchList();
    } else {
      alert(res.errors);
    }
  };

  const handleDeleteContender = async (contenderId: string) => {
    if (!window.confirm("Are you sure you want to delete this contender?")) return;
    const res = await fetchService.delete(endpoints.deleteContender, contenderId);
    if (res.success) {
      fetchList();
    } else {
      alert(res.errors);
    }
  };

  const handleRenameContender = async () => {
    if (!renamingContender || !tempName.trim()) return;
    const res = await fetchService.put(endpoints.renameContender, { title: tempName }, renamingContender.id);
    if (res.success) {
      setRenamingContender(null);
      fetchList();
    } else {
      alert(res.errors);
    }
  };

  const handleUploadImage = async (contenderId: string, file: File) => {
    setUploadingImageFor(contenderId);
    const res = await fetchService.uploadFile(endpoints.uploadContenderImage, file, contenderId);
    setUploadingImageFor(null);
    if (res.success) {
      fetchList();
    } else {
      alert(res.errors || "Failed to upload image.");
    }
  };

  const triggerFileUpload = (contenderId: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp,image/gif";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleUploadImage(contenderId, file);
      }
    };
    input.click();
  };

  const handleAddContenderImageFile = (file: File) => {
    setAddContenderImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setAddContenderImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleAddContenderImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAddContenderImageFile(file);
    }
  };

  const handleAddContenderPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        const file = items[i].getAsFile();
        if (file) {
          handleAddContenderImageFile(file);
          e.preventDefault();
          break;
        }
      }
    }
  };

  const handleAddContenderDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      handleAddContenderImageFile(file);
    }
  };

  const handleAddContenderPasteFromClipboard = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find((t) => t.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], "pasted-image.png", { type: imageType });
          handleAddContenderImageFile(file);
          return;
        }
      }
    } catch (err) {
      console.warn("Clipboard read permission denied or unsupported:", err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setShowCopySnackbar(true);
  };


  if (isLoading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const viewUrl = `${window.location.origin}/mashlist/${mashList?.viewShortKey}`;

  return (
    <div className={styles.mlContainer}>
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <h1 className={styles.h1}>{mashList.name}</h1>
            <IconButton onClick={() => { setTempName(mashList.name); setShowRenameListModal(true); }}>
              <EditIcon />
            </IconButton>
          </Box>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setShowDeleteListModal(true)}>
            Delete List
          </Button>
        </Box>

        <Card sx={{ mb: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <HowToVoteIcon color="secondary" />
              <Typography variant="h6">Public Voting</Typography>
            </Box>
            
            <Typography variant="body2" color="textSecondary" mb={2}>
              Share this link with others to let them vote on your mash list. No account required for voters!
            </Typography>

            <Box 
              display="flex" 
              alignItems="center" 
              gap={1} 
              p={1.5} 
              bgcolor="action.hover" 
              borderRadius={1}
              border="1px solid"
              borderColor="divider"
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  flexGrow: 1, 
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {viewUrl}
              </Typography>
              <Button 
                variant="contained" 
                size="small" 
                color="secondary"
                startIcon={<ContentCopyIcon />}
                onClick={() => copyToClipboard(viewUrl)}
              >
                Copy Link
              </Button>
            </Box>


          </CardContent>
        </Card>

        <Divider sx={{ mb: 2 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Contenders ({mashList?.contenders?.length || 0})</Typography>
          <Button variant="contained" onClick={() => setShowAddModal(true)}>Add Contender</Button>
        </Box>

        <div className={styles.listletContainerScroll}>
          <ul className={styles.ul}>
            {mashList?.contenders?.map((c) => (
              <li key={c.id}>
                {uploadingImageFor === c.id && <LinearProgress color="secondary" sx={{ mb: 1, borderRadius: 1 }} />}
                <Listlet 
                  numerator={c.rank} 
                  heading={c.title} 
                  description={c.description}
                  imageUrl={c.imageUrl}
                  score={c.score} 
                  deleteCallback={() => handleDeleteContender(c.id)}
                  renameCallback={() => { setRenamingContender(c); setTempName(c.title); }}
                  onUploadImage={() => triggerFileUpload(c.id)}
                />
              </li>
            ))}
          </ul>
        </div>

        <Box mt={3} textAlign="center">
          <Button variant="contained" size="large" href={`/mashlist/${props.id}/voting-session`}>Start Mashing</Button>
        </Box>
      </Paper>

      {/* RENAME LIST MODAL */}
      <Dialog open={showRenameListModal} onClose={() => setShowRenameListModal(false)}>
        <DialogTitle>Rename Mash List</DialogTitle>
        <DialogContent>
          <TextField 
            fullWidth 
            autoFocus 
            label="New Name" 
            value={tempName} 
            onChange={(e) => setTempName(e.target.value)} 
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRenameListModal(false)}>Cancel</Button>
          <Button onClick={handleRenameList} variant="contained">Rename</Button>
        </DialogActions>
      </Dialog>

      {/* DELETE LIST MODAL */}
      <Dialog open={showDeleteListModal} onClose={() => setShowDeleteListModal(false)}>
        <DialogTitle>Delete Mash List?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{mashList.name}"? This action is permanent.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteListModal(false)}>Cancel</Button>
          <Button onClick={handleDeleteList} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ADD CONTENDER MODAL */}
      <Dialog 
        open={showAddModal} 
        onClose={() => { setShowAddModal(false); setAddContenderImage(null); setAddContenderImagePreview(null); }}
        onPaste={handleAddContenderPaste}
        PaperProps={{ component: "form", onSubmit: handleAddContender }}
      >
        <DialogTitle>Add New Contender</DialogTitle>
        <DialogContent>
          <TextField fullWidth autoFocus name="name" label="Contender Name" required sx={{ mt: 1 }} />
          <TextField fullWidth name="description" label="Description (Optional)" multiline rows={3} sx={{ mt: 2 }} />
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" mb={1}>
              Image (Optional)
            </Typography>
            <Box
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleAddContenderDrop}
            >
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCameraIcon />}
                color="secondary"
                fullWidth
                sx={{ py: 1.5, borderStyle: 'dashed' }}
              >
                {addContenderImage ? addContenderImage.name : "Choose or Drag & Drop Image"}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAddContenderImageSelect}
                />
              </Button>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
              <Typography variant="caption" color="textSecondary">
                💡 Tip: You can paste an image with <strong>Ctrl+V</strong> / <strong>Cmd+V</strong>
              </Typography>
              {navigator.clipboard && 'read' in navigator.clipboard && (
                <Button
                  size="small"
                  variant="text"
                  color="secondary"
                  onClick={handleAddContenderPasteFromClipboard}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Paste from Clipboard
                </Button>
              )}
            </Box>

            {addContenderImagePreview && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <img
                  src={addContenderImagePreview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, objectFit: 'cover' }}
                />
                <Button 
                  size="small" 
                  color="error" 
                  onClick={() => { setAddContenderImage(null); setAddContenderImagePreview(null); }}
                  sx={{ mt: 0.5 }}
                >
                  Remove
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowAddModal(false); setAddContenderImage(null); setAddContenderImagePreview(null); }}>Cancel</Button>
          <Button type="submit" variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* RENAME CONTENDER MODAL */}
      <Dialog open={!!renamingContender} onClose={() => setRenamingContender(null)}>
        <DialogTitle>Rename Contender</DialogTitle>
        <DialogContent>
          <TextField 
            fullWidth 
            autoFocus 
            label="New Title" 
            value={tempName} 
            onChange={(e) => setTempName(e.target.value)} 
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenamingContender(null)}>Cancel</Button>
          <Button onClick={handleRenameContender} variant="contained">Rename</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showCopySnackbar}
        autoHideDuration={3000}
        onClose={() => setShowCopySnackbar(false)}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: { bgcolor: 'secondary.main' }
        }}
      />
    </div>
  );
}
